import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { CurrentUserQuery } from '@state/auth-state/current-user.query';
import { CurrentUserStore } from '@state/auth-state/current-user.store';
import { Subscription, finalize } from 'rxjs';

import { UserType } from '../enums/auth.enum';
import { AuthService, CurrentUserMediaQuery } from './auth.service';

const REFRESH_EARLY_MS = 180_000;
const FALLBACK_REFRESH_MS = 45 * 60_000;
const RETRY_REFRESH_MS = 5 * 60_000;

@Injectable({ providedIn: 'root' })
export class CurrentUserMediaRefreshService {
  private readonly authService = inject(AuthService);
  private readonly currentUserQuery = inject(CurrentUserQuery);
  private readonly currentUserStore = inject(CurrentUserStore);
  private readonly document = inject(DOCUMENT);

  private stateSubscription?: Subscription;
  private mediaRefreshSubscription?: Subscription;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private currentContextKey = '';
  private refreshInFlight = false;
  private started = false;
  private lastRefreshAt = 0;

  private readonly onVisibilityChange = (): void => {
    if (this.document.visibilityState !== 'visible') return;

    const user = this.currentUserQuery.getValue()?.data;
    if (this.isRefreshDue(user)) this.refresh();
  };

  start(): void {
    if (this.started) return;
    this.started = true;
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.stateSubscription = this.currentUserQuery
      .select(state => state.data)
      .subscribe(user => {
        if (!user?.id || !user?.userType) {
          this.clearTimer();
          this.mediaRefreshSubscription?.unsubscribe();
          this.mediaRefreshSubscription = undefined;
          this.refreshInFlight = false;
          this.currentContextKey = '';
          return;
        }

        const context = this.getMediaContext(user);
        if (this.currentContextKey !== context.key) {
          this.mediaRefreshSubscription?.unsubscribe();
          this.mediaRefreshSubscription = undefined;
          this.refreshInFlight = false;
          this.currentContextKey = context.key;
          this.refresh();
          return;
        }

        this.schedule(user);
      });
  }

  stop(): void {
    this.started = false;
    this.clearTimer();
    this.mediaRefreshSubscription?.unsubscribe();
    this.mediaRefreshSubscription = undefined;
    this.refreshInFlight = false;
    this.currentContextKey = '';
    this.lastRefreshAt = 0;
    this.stateSubscription?.unsubscribe();
    this.stateSubscription = undefined;
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private refresh(): void {
    const user = this.currentUserQuery.getValue()?.data;
    if (!user?.id || !user?.userType || this.refreshInFlight) return;

    this.clearTimer();
    this.refreshInFlight = true;
    const requestedContext = this.getMediaContext(user);
    this.mediaRefreshSubscription = this.authService
      .getCurrentUserMedia(requestedContext.query)
      .pipe(
        finalize(() => {
          this.refreshInFlight = false;
          this.mediaRefreshSubscription = undefined;
        })
      )
      .subscribe({
        next: response => {
          const current = this.currentUserQuery.getValue()?.data;
          const currentContext = this.getMediaContext(current);
          if (currentContext.key !== requestedContext.key) {
            this.refreshInFlight = false;
            this.refresh();
            return;
          }

          const media = response?.data;
          if (!media) {
            this.scheduleRetry();
            return;
          }

          this.refreshInFlight = false;
          this.lastRefreshAt = Date.now();
          this.currentUserStore.updateOne({ avatar: media.avatar });

          const latestCurrent = this.currentUserQuery.getValue()?.data;
          const latestContext = this.getMediaContext(latestCurrent);
          if (requestedContext.organizationId && latestContext.key === requestedContext.key) {
            this.currentUserStore.updateOrganization({ logo: media.logo });
          }
        },
        error: () => {
          this.refreshInFlight = false;
          const current = this.currentUserQuery.getValue()?.data;
          if (this.getMediaContext(current).key !== requestedContext.key) {
            this.refresh();
            return;
          }
          this.scheduleRetry();
        }
      });
  }

  private schedule(user: any): void {
    if (this.refreshInFlight) return;

    this.clearTimer();
    const expiries = this.getRefreshableUrls(user)
      .map(value => this.getSignedUrlExpiry(value))
      .filter((value): value is number => value !== null);
    const nextExpiry = expiries.length ? Math.min(...expiries) : null;
    const delay =
      nextExpiry === null
        ? FALLBACK_REFRESH_MS
        : Math.max(1_000, nextExpiry - Date.now() - REFRESH_EARLY_MS);

    this.refreshTimer = setTimeout(() => this.refresh(), delay);
  }

  private scheduleRetry(): void {
    this.clearTimer();
    this.refreshTimer = setTimeout(() => this.refresh(), RETRY_REFRESH_MS);
  }

  private isRefreshDue(user: any): boolean {
    if (!user?.id || !user?.userType || this.refreshInFlight) return false;

    const expiries = this.getRefreshableUrls(user)
      .map(value => this.getSignedUrlExpiry(value))
      .filter((value): value is number => value !== null);

    if (expiries.length) return Math.min(...expiries) <= Date.now() + REFRESH_EARLY_MS;
    return !this.lastRefreshAt || Date.now() - this.lastRefreshAt >= FALLBACK_REFRESH_MS;
  }

  private getRefreshableUrls(user: any): Array<string | null | undefined> {
    const context = this.getMediaContext(user);
    return context.organizationId ? [user?.avatar, user?.organization?.logo] : [user?.avatar];
  }

  private getMediaContext(user: any): {
    key: string;
    organizationId?: string;
    query: CurrentUserMediaQuery;
  } {
    const userId = user?.id || '';
    const organizationId = user?.organization?.id as string | undefined;
    const organizationType = this.getOrganizationType(user);
    const hasOrganizationContext = Boolean(organizationId && organizationType);
    const query = hasOrganizationContext ? { organizationId, organizationType } : {};

    return {
      key: `${userId}:${organizationType || ''}:${organizationId || ''}`,
      organizationId: hasOrganizationContext ? organizationId : undefined,
      query
    };
  }

  private getOrganizationType(user: any): CurrentUserMediaQuery['organizationType'] {
    switch (user?.viewAs) {
      case 'from_admin_to_brand':
        return UserType.Brand;
      case 'from_admin_to_factory':
        return UserType.Factory;
      case 'from_admin_to_consultant':
        return UserType.Consultant;
    }

    return user?.userType === UserType.Brand ||
      user?.userType === UserType.Factory ||
      user?.userType === UserType.Consultant
      ? user.userType
      : undefined;
  }

  private getSignedUrlExpiry(value?: string | null): number | null {
    if (!value) return null;

    const baseUrl = this.document.location?.origin || 'http://localhost';
    if (!URL.canParse(value, baseUrl)) return null;

    const url = new URL(value, baseUrl);
    const signedAt = url.searchParams.get('X-Amz-Date');
    const expiresIn = Number(url.searchParams.get('X-Amz-Expires'));
    if (!signedAt || !Number.isFinite(expiresIn) || expiresIn <= 0) return null;

    const match = signedAt.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
    if (!match) return null;

    const [, year, month, day, hour, minute, second] = match;
    const signedAtMs = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);
    return signedAtMs + expiresIn * 1_000;
  }

  private clearTimer(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = undefined;
  }
}
